// ═══════════════════════════════════════════════════════════════════════════
// syncJobProgramado — ¿este job está esperando su turno o está trabado?
// ═══════════════════════════════════════════════════════════════════════════
//
// FUENTE ÚNICA de la regla. La consumen los dos mundos:
//   · la UI (Live Sync Monitor, ESM)  → import normal
//   · el verificador de corridas (CJS, scripts/lib/veredictoSyncJobs.cjs)
//     → `await import(pathToFileURL(...))`, el mismo patrón que usa
//       scripts/seed-tipos-tarea-v1.cjs con catalogoTiposTarea.js
//
// Un job de cron nace `pending` y NO arranca enseguida A PROPÓSITO: lleva un
// jitter de 0 a 59 minutos (`cronOffsetMin`, hash FNV-1a del empresaId) que se
// aplica como `scheduleTime` del Cloud Task, para repartir mil empresas a lo
// largo de la ventana en vez de saturar el mismo segundo. Ese delay queda
// persistido en el doc del job como `delaySegundos` (`crearSyncJob` en
// functions/syncJobs.js; el hash en functions/syncCore/syncSchedule.js).
//
// Hasta el 4-ago-2026 el Live Sync Monitor mostraba `pending · hace 32m` sin
// distinguir los dos casos, y una corrida sana se leía como una fila de jobs
// colgados. El dato para desambiguar YA estaba en el documento.
//
// Nota: `functions/observabilidad/metricasTenant.js` resta `delaySegundos` con
// el mismo criterio para no contar el jitter como espera-en-cola (y disparar
// COLA_PROFUNDA falso). Es backend puro y no puede importar de src/, pero es el
// mismo razonamiento: el jitter es intencional, no es demora.
// ═══════════════════════════════════════════════════════════════════════════

// Tope del jitter que puede sembrar el motor (offset 0-59 min → 3540s). Sirve
// de cota defensiva: un `delaySegundos` disparatado en un doc no puede dejar un
// job "programado para siempre" y por lo tanto invisible al chequeo de stale.
export const JITTER_MAX_SEG = 3600

/** Normaliza los formatos de timestamp que llegan del SDK, de un callable o de admin. */
export function tsAMs(ts) {
  if (ts == null) return null
  if (typeof ts.toMillis === 'function') return ts.toMillis()
  if (typeof ts.toDate === 'function') return ts.toDate().getTime()
  if (typeof ts._seconds === 'number') return ts._seconds * 1000
  if (typeof ts.seconds === 'number') return ts.seconds * 1000
  const d = new Date(ts)
  return Number.isNaN(d.getTime()) ? null : d.getTime()
}

/**
 * @param {object} job — doc de syncJob (usa `creadoEn` y `delaySegundos`)
 * @param {number} [ahoraMs]
 * @returns {{ programado: boolean, arrancaEnMs: number|null, faltanMs: number|null }}
 *   `programado: true` ⇒ el job todavía no debía haber arrancado.
 */
export function ventanaProgramada(job, ahoraMs = Date.now()) {
  const creadoMs = tsAMs(job?.creadoEn)
  if (creadoMs === null) return { programado: false, arrancaEnMs: null, faltanMs: null }

  // Jobs anteriores al deploy del jitter no tienen el campo → 0. Un valor
  // negativo o basura tampoco puede adelantar el arranque ni retrasarlo más
  // allá del tope real del motor.
  // `Number.isFinite` SIN coerción, igual que functions/observabilidad/
  // metricasTenant.js y slaPorTenant.js: un string en ese campo es dato
  // corrupto, y tratarlo como 0 es el lado conservador (el job se evalúa
  // antes, nunca queda escondido detrás de un jitter inventado).
  const crudo = job?.delaySegundos
  const delay = Number.isFinite(crudo) ? Math.min(Math.max(crudo, 0), JITTER_MAX_SEG) : 0

  // Clock skew: si el doc dice que se creó en el futuro, contamos desde ahora.
  // Sin esto, un `creadoEn` adelantado dejaría el job "programado" para siempre
  // — otra vez un falso verde, que es justo lo que este módulo viene a evitar.
  const base = Math.min(creadoMs, ahoraMs)
  const arrancaEnMs = base + delay * 1000
  const faltanMs = arrancaEnMs - ahoraMs
  return { programado: faltanMs > 0, arrancaEnMs, faltanMs }
}

/**
 * Texto para la columna "Iniciado" de un job que todavía no arrancó.
 * Devuelve null si el job no está en ventana programada (el caller cae a su
 * formato habitual de tiempo relativo).
 */
export function etiquetaProgramado(job, ahoraMs = Date.now()) {
  if (job?.estado !== 'pending') return null
  const { programado, arrancaEnMs, faltanMs } = ventanaProgramada(job, ahoraMs)
  if (!programado) return null
  // HH:MM en 24h, hora local del navegador. Sin locale explícito a propósito:
  // un literal 'es-AR' acá rompe el ratchet multipaís (ADR 050 §14) y no aporta
  // nada — la hora de arranque de un job no se formatea distinto por país.
  const hora = new Date(arrancaEnMs).toTimeString().slice(0, 5)
  return {
    texto: `programado ${hora}`,
    detalle: `arranca en ~${Math.max(1, Math.round(faltanMs / 60000))} min (jitter de cron, no está trabado)`,
  }
}
